import { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Eye, EyeOff, ArrowLeft, Loader2, Newspaper,
  ImagePlus, ExternalLink, Save, X,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absImg = (src) => (!src ? null : (src.startsWith('http') ? src : `${BACKEND}${src}`));

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const toBrusselsInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d);
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`;
};

const fmtScheduled = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('fr-BE', { timeZone: 'Europe/Brussels', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
};

const articleStatus = (a) => {
  if (a.published) return { label: 'Publié', cls: 'bg-emerald-50 text-emerald-600' };
  if (a.scheduled_at) return { label: `Programmé — ${fmtScheduled(a.scheduled_at)}`, cls: 'bg-blue-50 text-blue-600' };
  return { label: 'Brouillon', cls: 'bg-amber-50 text-amber-600' };
};

const EMPTY = { title: '', slug: '', meta_title: '', meta_description: '', excerpt: '', content: '', cover_image: '', published: false, scheduled_at: '' };

export function BlogTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | edit
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [slugEdited, setSlugEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/blog');
      setArticles(data.articles || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setSlugEdited(false); setShowPreview(false); setMode('edit'); };
  const openEdit = (a) => {
    setForm({ title: a.title || '', slug: a.slug || '', meta_title: a.meta_title || '', meta_description: a.meta_description || '', excerpt: a.excerpt || '', content: a.content || '', cover_image: a.cover_image || '', published: !!a.published, scheduled_at: toBrusselsInput(a.scheduled_at) });
    setEditingId(a.id); setSlugEdited(true); setShowPreview(false); setMode('edit');
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onTitleChange = (v) => {
    setForm((f) => ({ ...f, title: v, slug: slugEdited ? f.slug : slugify(v) }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('files', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const urls = data.urls || [];
      if (urls[0]) { setField('cover_image', urls[0]); toast.success('Image téléchargée'); }
      else toast.error('Format non supporté');
    } catch { toast.error('Erreur de téléchargement'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/blog/${editingId}`, form);
        toast.success('Article enregistré');
      } else {
        const { data } = await api.post('/admin/blog', form);
        setEditingId(data.id);
        toast.success('Article créé (brouillon)');
      }
      await load();
      setMode('list');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const togglePublish = async (a) => {
    try {
      await api.post(`/admin/blog/${a.id}/${a.published ? 'unpublish' : 'publish'}`);
      toast.success(a.published ? 'Article dépublié' : 'Article publié');
      load();
    } catch { toast.error('Erreur'); }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success('Article supprimé');
      setConfirmDelete(null);
      load();
    } catch { toast.error('Erreur'); }
  };

  // ─── Edit view ───
  if (mode === 'edit') {
    const metaLen = form.meta_description.length;
    const cover = absImg(form.cover_image);
    return (
      <div className="space-y-5" data-testid="blog-editor">
        <div className="flex items-center justify-between">
          <button onClick={() => setMode('list')} className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800" data-testid="blog-editor-back">
            <ArrowLeft className="w-4 h-4" /> Retour à la liste
          </button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowPreview((p) => !p)} data-testid="blog-editor-preview-toggle">
              <Eye className="w-4 h-4 mr-1.5" /> {showPreview ? 'Masquer l\'aperçu' : 'Aperçu'}
            </Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#F25C05] hover:bg-[#d94f04] text-white" data-testid="blog-editor-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Enregistrer</>}
            </Button>
          </div>
        </div>

        <div className={`grid gap-6 ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
          {/* Form */}
          <div className="space-y-4">
            <Field label="Titre de l'article (H1)">
              <input value={form.title} onChange={(e) => onTitleChange(e.target.value)} className={inputCls} placeholder="Ex: Stock dormant : ce qu'il coûte vraiment" data-testid="blog-field-title" />
            </Field>

            <Field label="Slug (URL)" hint={`/blog/${form.slug || '...'}`}>
              <input value={form.slug} onChange={(e) => { setSlugEdited(true); setField('slug', e.target.value); }} className={inputCls} placeholder="stock-dormant-cout" data-testid="blog-field-slug" />
            </Field>

            <Field label="Meta title (SEO)">
              <input value={form.meta_title} onChange={(e) => setField('meta_title', e.target.value)} className={inputCls} placeholder="Stock dormant : ce qu'il coûte vraiment | DestockPro" data-testid="blog-field-meta-title" />
            </Field>

            <Field label="Meta description (SEO)" hint={<span className={metaLen > 160 ? 'text-red-500 font-semibold' : 'text-neutral-400'}>{metaLen}/160</span>}>
              <textarea value={form.meta_description} onChange={(e) => setField('meta_description', e.target.value)} rows={2} className={inputCls} placeholder="Description affichée dans les résultats Google (max ~160 caractères)" data-testid="blog-field-meta-desc" />
            </Field>

            <Field label="Extrait (aperçu dans la liste)">
              <textarea value={form.excerpt} onChange={(e) => setField('excerpt', e.target.value)} rows={2} className={inputCls} placeholder="Phrase d'accroche affichée sur la page /blog" data-testid="blog-field-excerpt" />
            </Field>

            <Field label="Image de couverture">
              <div className="flex items-center gap-3">
                {cover && <img src={cover} alt="" className="w-24 h-16 object-cover rounded-lg border dark:border-neutral-700" />}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" id="blog-cover-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="blog-field-cover-upload">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImagePlus className="w-4 h-4 mr-1.5" /> {cover ? 'Changer' : 'Ajouter'}</>}
                </Button>
                {cover && <Button type="button" variant="ghost" size="sm" onClick={() => setField('cover_image', '')} className="text-red-500"><X className="w-4 h-4" /></Button>}
              </div>
            </Field>

            <Field label="Programmer la publication" hint="Fuseau Europe/Bruxelles — vide = brouillon">
              <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setField('scheduled_at', e.target.value)} className={inputCls} data-testid="blog-field-scheduled" />
              {form.scheduled_at && !form.published && (
                <p className="text-[11px] text-blue-600 mt-1">Publication automatique programmée. L'article reste en brouillon jusque-là.</p>
              )}
            </Field>

            <Field label="Contenu (Markdown)" hint="Titres ##, listes, **gras**, tableaux GFM">
              <textarea value={form.content} onChange={(e) => setField('content', e.target.value)} rows={20} className={`${inputCls} font-mono text-xs leading-relaxed`} placeholder={'## Sous-titre\n\nUn paragraphe...\n\n- élément de liste'} data-testid="blog-field-content" />
            </Field>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="lg:sticky lg:top-4 self-start">
              <p className="text-xs font-medium text-neutral-500 mb-2">Aperçu du rendu</p>
              <div className="border dark:border-neutral-700 rounded-xl p-5 bg-white max-h-[70vh] overflow-y-auto">
                <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-4">{form.title || 'Titre de l\'article'}</h1>
                <div className="blog-content" data-testid="blog-editor-preview">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || '_Le contenu apparaîtra ici…_'}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── List view ───
  return (
    <div className="space-y-4" data-testid="blog-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold text-neutral-900 dark:text-white">Articles du blog</h2>
          <p className="text-xs text-neutral-500">{articles.length} article(s) — {articles.filter((a) => a.published).length} publié(s)</p>
        </div>
        <Button size="sm" onClick={openNew} className="bg-[#1F3F72] hover:bg-[#16305a] text-white" data-testid="blog-new-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvel article
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700">
          <Newspaper className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500">Aucun article. Créez le premier.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map((a) => (
            <div key={a.id} className="flex items-center gap-3 bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-3" data-testid={`blog-row-${a.slug}`}>
              <div className="w-16 h-12 rounded-lg bg-[#1F3F72] shrink-0 overflow-hidden flex items-center justify-center">
                {absImg(a.cover_image) ? <img src={absImg(a.cover_image)} alt="" className="w-full h-full object-cover" /> : <Newspaper className="w-4 h-4 text-white/40" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {(() => { const s = articleStatus(a); return (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${s.cls}`}>{s.label}</span>
                  ); })()}
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate">{a.title}</p>
                </div>
                <p className="text-[11px] text-neutral-400 truncate">/blog/{a.slug}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {a.published && (
                  <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-neutral-400 hover:text-[#1F3F72]" title="Voir" data-testid={`blog-view-${a.slug}`}>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button onClick={() => togglePublish(a)} className="p-2 text-neutral-400 hover:text-[#F25C05]" title={a.published ? 'Dépublier' : 'Publier'} data-testid={`blog-toggle-${a.slug}`}>
                  {a.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => openEdit(a)} className="p-2 text-neutral-400 hover:text-[#1F3F72]" title="Modifier" data-testid={`blog-edit-${a.slug}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                {confirmDelete === a.id ? (
                  <div className="flex items-center gap-1">
                    <button onClick={() => doDelete(a.id)} className="text-[11px] font-bold text-red-600 px-2" data-testid={`blog-confirm-delete-${a.slug}`}>Confirmer</button>
                    <button onClick={() => setConfirmDelete(null)} className="text-[11px] text-neutral-400 px-1">Annuler</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDelete(a.id)} className="p-2 text-neutral-400 hover:text-red-500" title="Supprimer" data-testid={`blog-delete-${a.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inputCls = 'w-full border dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:ring-2 focus:ring-[#F25C05]/30 focus:border-[#F25C05] outline-none';

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400">{label}</label>
        {hint && <span className="text-[11px] text-neutral-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
